import { useState } from "react";

// Updated Full ASCII Mapping (0-126)
const asciiMapping = {
  0: "NUL", 1: "SOH", 2: "STX", 3: "ETX", 4: "EOT", 5: "ENQ", 6: "ACK", 7: "BEL",
  8: "BS", 9: "HT", 10: "LF", 11: "VT", 12: "FF", 13: "CR", 14: "SO", 15: "SI",
  16: "DLE", 17: "DC1", 18: "DC2", 19: "DC3", 20: "DC4", 21: "NAK", 22: "SYN",
  23: "ETB", 24: "CAN", 25: "EM", 26: "SUB", 27: "ESC", 28: "FS", 29: "GS",
  30: "RS", 31: "US", 32: "SP", 33: "!", 34: '"', 35: "\u00A3", 36: "$", 37: "%",
  38: "&", 39: "'", 40: "(", 41: ")", 42: "*", 43: "+", 44: ",", 45: "-", 46: ".",
  47: "/", 48: "0", 49: "1", 50: "2", 51: "3", 52: "4", 53: "5", 54: "6", 55: "7",
  56: "8", 57: "9", 58: ":", 59: ";", 60: "<", 61: "=", 62: ">", 63: "?", 64: "@",
  65: "A", 66: "B", 67: "C", 68: "D", 69: "E", 70: "F", 71: "G", 72: "H", 73: "I",
  74: "J", 75: "K", 76: "L", 77: "M", 78: "N", 79: "O", 80: "P", 81: "Q", 82: "R",
  83: "S", 84: "T", 85: "U", 86: "V", 87: "W", 88: "X", 89: "Y", 90: "Z", 91: "[",
  92: "\\", 93: "]", 94: "^", 95: "_", 96: "`", 97: "a", 98: "b", 99: "c",
  100: "d", 101: "e", 102: "f", 103: "g", 104: "h", 105: "i", 106: "j", 107: "k",
  108: "l", 109: "m", 110: "n", 111: "o", 112: "p", 113: "q", 114: "r", 115: "s",
  116: "t", 117: "u", 118: "v", 119: "w", 120: "x", 121: "y", 122: "z", 123: "{",
  124: "|", 125: "}", 126: "~"
};

// Token Mapping
const tokenMapping = {
  128: "GWT", 129: "NWT", 130: "PST", 131: "UN2", 132: "UN", 133: "CON",
  134: "BCC", 135: "ID", 136: "TIM", 137: "DAT", 138: "GWU", 139: "RCN",
  140: "AXL", 141: "PPT", 142: "CLA", 143: "CHA", 144: "ZRO", 145: "MOT",
  146: "DSP", 147: "DSW", 148: "PT", 149: "STA", 151: "GTO", 152: "GTU",
  153: "STO", 154: "STU", 155: "PLU", 156: "DES", 157: "PRT", 158: "PF3",
  159: "PTT", 160: "DST", 171: "GN1", 172: "GN2", 173: "WST", 174: "UFT",
  175: "UFD1", 176: "UFD2", 177: "UFD3", 178: "UFD4", 179: "UFD5",
  180: "ULD", 181: "HEI", 201: "PL1", 202: "PL2", 203: "PLT", 204: "ALI",
  205: "CRC", 206: "PLA", 207: "PLS", 255: "EOS"
};

const reverseTokenMap = Object.fromEntries(
  Object.entries(tokenMapping).map(([k, v]) => [v.toLowerCase(), Number(k)])
);

export default function App() {
  const [inputText, setInputText] = useState("");
  const [results, setResults] = useState([]);
  const [combinedDecimal, setCombinedDecimal] = useState("");
  const [combinedName, setCombinedName] = useState("");

  const handleSearch = () => {
    if (!inputText.trim()) return;

    const input = inputText.trim();
    const output = [];
    const decimalArray = [];
    const nameArray = [];

    if (/^\d+(\s+\d+)*$/.test(input)) {
      const numbers = input.split(/\s+/).map(Number);
      for (const num of numbers) {
        const token = tokenMapping[num] || asciiMapping[num] || "?";
        output.push({
          letter: token,
          decimal: num,
          character: asciiMapping[num] || "-",
          token: tokenMapping[num] || "-"
        });
        decimalArray.push(num);
        nameArray.push(token);
      }
    } else {
      const words = input.split("");
      for (const letter of words) {
        if (letter === " ") {
          output.push({ letter: " ", decimal: 32, character: "SP", token: "-" });
          decimalArray.push(32);
          nameArray.push("SP");
        } else {
          const decimal = letter.charCodeAt(0);
          output.push({
            letter,
            decimal,
            character: asciiMapping[decimal] || "-",
            token: tokenMapping[decimal] || "-"
          });
          decimalArray.push(decimal);
          nameArray.push(asciiMapping[decimal] || letter);
        }
      }
    }

    setResults(output);
    setCombinedDecimal(decimalArray.join(" "));
    setCombinedName(nameArray.join(" "));
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    alert(`Copied: ${text}`);
  };

  return (
    <div className="min-h-screen bg-gray-100 mt-28 flex flex-col items-center p-6">
      <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-3xl">
        <h1 className="text-2xl font-bold mb-6 text-center">Word and Token Code Converter</h1>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center justify-center mb-6">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
            placeholder="Enter text (e.g. G EOS or 71 32 255)"
            className="border p-3 rounded-lg text-xl w-full sm:w-2/3"
          />
          <button
            onClick={handleSearch}
            className="bg-orange-500 hover:bg-orange-600 text-white py-2 px-6 rounded-full font-semibold"
          >
            Convert
          </button>
        </div>

        {results.length > 0 && (
          <div className="overflow-x-auto animate-fadeIn">
            <table className="min-w-full table-auto border border-gray-300 mb-6">
              <thead className="bg-black text-white">
                <tr>
                  <th className="py-2 px-4 border">Letter/Token</th>
                  <th className="py-2 px-4 border">Decimal</th>
                  <th className="py-2 px-4 border">Character</th>
                  <th className="py-2 px-4 border">Token</th>
                  <th className="py-2 px-4 border">Copy</th>
                </tr>
              </thead>
              <tbody>
                {results.map((item, idx) => (
                  <tr key={idx} className="text-center border-t">
                    <td className="py-2 px-4 border">{item.letter}</td>
                    <td className="py-2 px-4 border">{item.decimal}</td>
                    <td className="py-2 px-4 border">{item.character}</td>
                    <td className="py-2 px-4 border">{item.token}</td>
                    <td className="py-2 px-4 border">
                      <button
                        onClick={() => handleCopy(item.decimal)}
                        className="bg-black hover:bg-gray-800 text-white py-1 px-3 rounded-full text-sm"
                      >
                        Copy Decimal
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="bg-gray-200 p-4 rounded-lg flex flex-col gap-4">
              <div className="font-semibold text-center">Combined Decimal Output:</div>
              <div className="bg-white p-3 rounded-lg text-center font-mono text-lg shadow-md">{combinedDecimal}</div>
              <button
                onClick={() => handleCopy(combinedDecimal)}
                className="bg-orange-500 hover:bg-orange-600 text-white py-2 px-6 rounded-full self-center font-semibold"
              >
                Copy Decimal
              </button>

              <div className="font-semibold text-center mt-6">Combined Name Output:</div>
              <div className="bg-white p-3 rounded-lg text-center font-mono text-lg shadow-md">{combinedName}</div>
              <button
                onClick={() => handleCopy(combinedName)}
                className="bg-orange-500 hover:bg-orange-600 text-white py-2 px-6 rounded-full self-center font-semibold"
              >
                Copy Name
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
